// Regras de movimento compartilhadas entre o navegador e o servidor.

/** Direções no padrão do Tibia: 0 norte, 1 leste, 2 sul, 3 oeste, 4..7 diagonais. */
export const DIR_DELTA: ReadonlyArray<readonly [number, number]> = [
  [0, -1], [1, 0], [0, 1], [-1, 0], [1, -1], [1, 1], [-1, 1], [-1, -1],
];

export function dirFromDelta(dx: number, dy: number): number {
  const i = DIR_DELTA.findIndex(([a, b]) => a === Math.sign(dx) && b === Math.sign(dy));
  return i < 0 ? 2 : i;
}

/** Direção do sprite (só as 4 principais) para uma direção qualquer. */
export function lookDir(dir: number): number {
  if (dir < 4) return dir;
  return dir === 4 || dir === 5 ? 1 : 3; // diagonais olham para leste/oeste, como no cliente
}

/** Duração de um passo em ms. groundSpeed vem do chão (100 = normal); speed é a velocidade exibida. */
export function stepDuration(groundSpeed: number, speed: number, diagonal = false): number {
  const base = Math.max(1, groundSpeed) * 1000 / Math.max(10, speed * 2);
  return Math.max(80, Math.round(base * (diagonal ? 2 : 1)));
}

export interface Position { x: number; y: number; f: number }

export interface Creature {
  id: string;
  kind: 'player' | 'monster' | 'npc';
  name: string;
  outfit: { lookType: number; head: number; body: number; legs: number; feet: number; addons: number; mount?: number };
  x: number; y: number; f: number; dir: number;
  speed: number;
  hp: number; // porcentagem
  /** monstro especial: 1 Super (bronze), 2 Mega (prata), 3 Ultra (dourado) */
  tier?: 1 | 2 | 3;
  /** aura cosmética do jogador (cor), ex.: recompensa final do passe de batalha */
  aura?: number;
  /** aparência temporária de avatar (Wheel / Transcendence): o outfit escolhido continua o mesmo */
  avatar?: boolean;
  /** PvP: skull do jogador */
  skull?: 'white' | 'red' | 'black';
}

export interface HuntInfo {
  id: string; name: string; theme: string; level: number; monsters: string[];
  floors: number; size: [number, number]; spawns: number; monstersTotal: number;
  /** boxes por andar: { andar: { tamanho da box: quantidade } } */
  boxes?: Record<number, Record<number, number>>;
  /** quantos monstros de cada tipo a hunt tem (todos os andares) */
  mix?: Record<string, number>;
  /** detalhes dos monstros para o menu de hunts (aparência, vida, XP e loots mais raros) */
  /** criatura do dia nesta hunt / boss do dia é o desta hunt */
  boostCreature?: string; boostBoss?: string;
  details?: Array<{ name: string; outfit: { lookType: number; head: number; body: number; legs: number; feet: number; addons: number }; hp: number; exp: number; rare: number[]; loot?: number[] }>;
}

/** O depot só pode ser usado dentro do templo ou do prédio do depot (retângulos dos prédios no mapa da cidade). */
export function atDepotArea(labels: Array<{ text: string; kind: string; rect?: number[] }> | undefined, x: number, y: number): boolean {
  return (labels ?? []).some((l) => l.rect && (l.kind === 'depot' || l.text === 'Templo de Noxara')
    && x >= l.rect[0]! && x <= l.rect[2]! && y >= l.rect[1]! && y <= l.rect[3]!);
}

/** Tamanhos de box que existem em cada andar das hunts. */
export const BOX_SIZES: Record<number, number[]> = { 0: [2, 4, 5, 6], 1: [5, 6, 7], 2: [6, 7, 8] };
export const ALL_BOX_SIZES = [2, 4, 5, 6, 7, 8];

/** Portais do Salão dos Portais: os mais perto da porta (embaixo) levam às hunts de level mais baixo. */
export function portalAssignments(portals: number[][], hunts: Array<{ id: string; level: number }>): Map<string, string> {
  const sortedP = [...portals].sort((a, b) => b[1]! - a[1]! || a[0]! - b[0]!);
  const sortedH = [...hunts].sort((a, b) => a.level - b.level || a.id.localeCompare(b.id));
  const out = new Map<string, string>();
  sortedH.forEach((h, i) => { const p = sortedP[i]; if (p) out.set(`${p[0]},${p[1]},${p[2] ?? 0}`, h.id); });
  return out;
}

/** Abates num andar para liberar começar direto no andar seguinte (proposta). */
export const FLOOR_UNLOCK_KILLS = 500;

export function floorUnlocked(progress: number[] | undefined, floor: number): boolean {
  return floor <= 0 || (progress?.[floor - 1] ?? 0) >= FLOOR_UNLOCK_KILLS;
}

/** Hunt de evento: uma por semana, com XP em dobro no sábado e no domingo (horário de Brasília). */
export function eventHunt(huntIds: string[], now = Date.now()): { id: string; active: boolean } | null {
  if (!huntIds.length) return null;
  const br = new Date(now - 3 * 3600_000);
  const week = Math.floor((br.getTime() / 86400_000 + 3) / 7); // semanas começando na segunda
  const ids = [...huntIds].sort();
  const day = br.getUTCDay();
  return { id: ids[week % ids.length]!, active: day === 6 || day === 0 };
}

/** Abates de monstros numa hunt para nascer o boss da hunt (proposta). */
export const BOSS_EVERY_KILLS = 800;
